"use client";

import { type ReactNode } from "react";

import { liteClient as algoliasearch } from "algoliasearch/lite";
import { type UiState } from "instantsearch.js";
import {
  InstantSearchNext,
} from "react-instantsearch-nextjs";

import { getEnv } from "@/shared.utils/env";

import { type ProductListContextProps } from "../feat.product-list-context";
import { hierarchicalMenuMapper } from "./hierarchical-menu-mapper";
import { queryParse, queryStringify } from "./qs-modules";
import { rangeMapper } from "./range-mapper";
import { refinementListMapper } from "./refinement-list-mapper";
import { searchBoxMapper } from "./search-box-mapper";
import { sortByMapper } from "./sort-by-mapper";
import { toggleRefinementMapper } from "./toggle-refinement-mapper";
import { type QueryParams } from "./typed";

const searchClient = algoliasearch(
  getEnv(
    process.env.NEXT_PUBLIC_SEARCH_APP_ID,
    "NEXT_PUBLIC_SEARCH_APP_ID",
  ),
  getEnv(
    process.env.NEXT_PUBLIC_SEARCH_KEY,
    "NEXT_PUBLIC_SEARCH_KEY",
  ),
);

interface InstantSearchProps extends ProductListContextProps {
  children: ReactNode;
}
const ProductInstantSearch = (props: InstantSearchProps) => {
  const {
    children,
    categoryAttribute,
    categoryVariant,
    sortByItems,
    hierarchicalMenuAttributes,
    fixedRange,
  } = props;

  // specify index name
  const indexName = getEnv(
    process.env.NEXT_PUBLIC_SEARCH_INDEX_PRODUCT,
    "NEXT_PUBLIC_SEARCH_INDEX_PRODUCT",
  );

  const {
    createURL: sortByMapCreateURL,
    parseURL: sortByMapParseURL,
  } = sortByMapper({ sortByItems });
  const {
    createURL: refinementListMapCreateURL,
    parseURL: refinementListMapParseURL,
  } = refinementListMapper({
    categoryAttribute,
    categoryVariant,
  });
  const {
    createURL: hierarchicalMenuMapCreateURL,
    parseURL: hierarchicalMenuMapParseURL,
  } = hierarchicalMenuMapper({
    hierarchicalMenuAttributes,
  });
  const {
    createURL: searchBoxMapCreateURL,
    parseURL: searchBoxMapParseURL,
  } = searchBoxMapper();
  const {
    createURL: rangeMapCreateURL,
    parseURL: rangeMapParseURL,
  } = rangeMapper({
    categoryAttribute,
    categoryVariant,
    fixedRange,
  });
  const {
    createURL: toggleRefinementMapCreateURL,
    parseURL: toggleRefinementMapParseURL,
  } = toggleRefinementMapper({
    categoryVariant,
  });

  return (
    <InstantSearchNext
      indexName={indexName}
      searchClient={searchClient}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      routing={{
        router: {
          cleanUrlOnDispose: false,

          createURL: ({
            routeState,
            location,
          }) => {
            // deconstruct props
            const { origin, pathname } = location;
            const {
              [indexName]: {
                hierarchicalMenu,
                refinementList,
                sortBy,
                query,
                range,
                toggle,
              },
            }: UiState = routeState;

            // create pathname
            const categoryQueryParams = hierarchicalMenuMapCreateURL(
              hierarchicalMenu,
            );

            // create query params
            const refinementListQueryParams = refinementListMapCreateURL(
              refinementList,
            );
            const sortByQueryParams = sortByMapCreateURL(
              sortBy,
            );
            const searchBoxQueryParams = searchBoxMapCreateURL(
              query,
            );
            const rangeQueryParams = rangeMapCreateURL(
              range,
            );
            const toggleRefinementQueryParams = toggleRefinementMapCreateURL(
              toggle,
            );
            const queryParams: QueryParams = {
              ...categoryQueryParams,
              ...refinementListQueryParams,
              ...sortByQueryParams,
              ...searchBoxQueryParams,
              ...rangeQueryParams,
              ...toggleRefinementQueryParams,
            };

            // stringify query params
            const queryString = queryStringify(queryParams);

            return `${origin}${pathname}${queryString}`;
          },

          parseURL: ({ location }) => {
            // deconstruct props
            const { pathname, search: queryString } = location;

            // parse query params
            const queryParams = queryParse(queryString);

            // create ui state
            const query = searchBoxMapParseURL(queryParams);
            const refinementList = refinementListMapParseURL(
              pathname,
              queryParams,
            );
            const sortBy = sortByMapParseURL(queryParams);
            const hierarchicalMenu = hierarchicalMenuMapParseURL(
              queryParams,
            );
            const range = rangeMapParseURL(
              queryParams,
            );
            const toggle = toggleRefinementMapParseURL(
              queryParams,
            );

            const uiState: UiState = {
              [indexName]: {
                hierarchicalMenu,
                refinementList,
                sortBy,
                query,
                range,
                toggle,
              },
            };

            return uiState;
          },
        },
      }}
    >
      {children}
    </InstantSearchNext>
  );
};

export { ProductInstantSearch };
